import { STORE_LABEL_CONFIG } from "./site";
import { formatRupiah } from "./format";

const DEFAULT_ITEM_WEIGHT_GRAMS = 500;

/**
 * Calculate total weight of order items by looking up product weights.
 */
const calculateTotalWeight = (sale, products) => {
  return sale.items.reduce((total, item) => {
    const product = products.find((p) => String(p.id) === String(item.product_id));
    const itemWeight = (product?.weight || DEFAULT_ITEM_WEIGHT_GRAMS) * item.quantity;
    return total + itemWeight;
  }, 0);
};

/**
 * Format customer address for the shipping label.
 * Combines street, area (kelurahan/kecamatan/kota), and province+zip into structured lines.
 */
const formatCustomerAddress = (customer) => {
  const lines = [];

  if (customer.address) lines.push(customer.address);

  const area = [customer.kelurahan, customer.kecamatan, customer.kota].filter(Boolean).join(", ");
  if (area) lines.push(area);

  const provinceLine = [customer.province, customer.zip_code].filter(Boolean).join(" ");
  if (provinceLine) lines.push(provinceLine);

  return lines.join("<br>");
};

/**
 * Generate HTML table rows for order items.
 */
const generateItemsRows = (items) =>
  items
    .map(
      (item) => `
    <tr>
      <td>${item.product_name_snapshot}</td>
      <td align="center">${item.quantity}</td>
    </tr>`,
    )
    .join("");

/**
 * Open a new window with the shipping label HTML and trigger browser print dialog.
 * Only works when payment_status is "dibayar" — COD shows Rp 0 since already paid.
 */
export const printShippingLabel = (sale, products) => {
  const store = STORE_LABEL_CONFIG;
  const invoice = sale.invoice;
  const barcodeData = invoice.replace(/-/g, "");
  const codAmount = sale.payment_status === "dibayar" ? 0 : sale.total;
  const totalWeight = calculateTotalWeight(sale, products);
  const weightDisplay =
    totalWeight >= 1000 ? `${(totalWeight / 1000).toFixed(1)} kg` : `${totalWeight} gr`;

  const storeAddressHtml = store.storeAddress.replace(/\n/g, "<br>");

  const html = `<!DOCTYPE html>
<html lang="id">
<head>
    <meta charset="UTF-8">
    <title>Label Pengiriman - ${invoice}</title>

    <style>
        @page {
            size: 80mm 120mm;
            margin: 3mm;
        }

        * {
            box-sizing: border-box;
        }

        body {
            margin: 0;
            font-family: Arial, Helvetica, sans-serif;
            background: #fff;
        }

        .label {
            width: 100%;
            border: 2px solid #000;
            padding: 6px;
        }

        .header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 5px;
            margin-bottom: 5px;
        }

        .header h2 {
            margin: 0;
            font-size: 14px;
        }

        .resi {
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            letter-spacing: 1px;
            margin: 6px 0;
        }

        .section {
            margin-bottom: 6px;
        }

        .title {
            font-size: 9px;
            font-weight: bold;
            margin-bottom: 2px;
            text-transform: uppercase;
        }

        .content {
            font-size: 11px;
            line-height: 1.3;
        }

        .receiver-name {
            font-size: 14px;
            font-weight: bold;
        }

        .address {
            font-size: 11px;
        }

        .footer {
            border-top: 2px dashed #000;
            padding-top: 5px;
            margin-top: 6px;
            display: flex;
            justify-content: space-between;
        }

        .small {
            font-size: 10px;
        }

        .barcode {
            text-align: center;
            margin-top: 6px;
        }

        .barcode img {
            max-width: 100%;
            height: 36px;
        }

        @media print {
            .no-print {
                display: none;
            }
        }
    </style>
</head>
<body>

<div class="label">

    <div class="header">
        <h2>${store.storeName}</h2>
        <div>Label Pengiriman</div>
    </div>

    <div class="resi">
        ${invoice}
    </div>

    <div class="section">
        <div class="title">Pengirim</div>
        <div class="content">
            <strong>${store.storeName}</strong><br>
            ${storeAddressHtml}<br>
            ${store.storePhone}
        </div>
    </div>

    <div class="section">
        <div class="title">Penerima</div>

        <div class="receiver-name">
            ${sale.customer.full_name}
        </div>

        <div class="address">
            ${formatCustomerAddress(sale.customer)}
        </div>

        <div style="margin-top:5px;">
            <strong>HP:</strong> ${sale.customer.phone}
        </div>

        ${sale.courier_name ? `<div style="margin-top:4px; font-size:14px; font-weight:bold; background:#f0f0f0; padding:4px 8px; border-radius:4px; display:inline-block;">
            📦 ${sale.courier_name}${sale.courier_service ? ` - ${sale.courier_service}` : ""}
        </div>` : ""}
    </div>

    <div class="section">
        <table width="100%" border="1" cellspacing="0" cellpadding="4">
            <tr>
                <th>Produk</th>
                <th>Qty</th>
            </tr>
            ${generateItemsRows(sale.items)}
        </table>
    </div>

    <div class="barcode">
        <img src="https://barcode.tec-it.com/barcode.ashx?data=${barcodeData}&code=Code128" alt="${invoice}">
    </div>

    <div class="footer">
        <div class="small">
            COD : ${formatRupiah(codAmount)}
        </div>

        <div class="small">
            Berat : ${weightDisplay}
        </div>
    </div>

</div>

<div class="no-print" style="text-align:center; margin-top:15px; padding:10px;">
    <button onclick="window.print()" style="padding:10px 24px; cursor:pointer; font-size:14px; margin-right:8px;">Cetak Lagi</button>
    <button onclick="window.close()" style="padding:10px 24px; cursor:pointer; font-size:14px;">Tutup Window</button>
</div>

</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=420,height=640");
  if (!printWindow) {
    alert("Pop-up diblokir oleh browser. Izinkan pop-up untuk mencetak label pengiriman.");
    return;
  }

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  // Delay print slightly to allow barcode image to start loading
  setTimeout(() => {
    printWindow.print();
  }, 800);
};